import { FC, useCallback, useMemo, useState } from 'react';

import { useGetLeads } from 'modules/account/contact/hooks';
import { useGetSubscription } from 'modules/account/settings/hooks';
import DatePicker from 'react-datepicker';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { Button } from 'ui/components/common';
import { Checkbox, FilterForm } from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import useModal from 'ui/partials/Modal/useModal';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import constants, { EVENT_TABS, EVENT_TYPES, SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import {
  Can as CanRolePermissions,
  useCan as useCanRolePermissions,
} from 'utils/providers/RolePermission';
import { dayjs } from 'utils/providers/Timezone';
import SelectAvatarPopover from 'ui/components/form/SelectAvatar/SelectAvatarPopover';
import { useQueryParams } from 'hooks';
import { View } from 'react-big-calendar';

import { BigCalendar, CreateEventModal } from '../../components';
import useGetEvents, { Params } from '../../hooks/useGetEvents';
import CalendarContext from '../../utils/providers/CalendarContext';

const { ACTIONS, ACCESS_LEVEL, PERMISSION_KEYS } = constants;

const CALENDAR_TABS = Object.keys(EVENT_TABS).map((tab) => tab);

export type CalendarView = 'day' | 'week' | 'month' | 'agenda' | 'custom';

type BaseDateParams = {
  year?: number;
  month?: number;
  day?: number;
};

type StandardViewParams = BaseDateParams & {
  view?: Exclude<CalendarView, 'custom'>;
};

type CustomDaysView = BaseDateParams & {
  view: 'custom';
  days: number;
  weeks?: never;
};

type CustomWeeksView = BaseDateParams & {
  view: 'custom';
  weeks: number;
  days?: never;
};

type CalendarQueryKeys = 'view' | 'days' | 'weeks' | 'year' | 'month' | 'day';

export type CalendarQueryParams =
  | StandardViewParams
  | CustomDaysView
  | CustomWeeksView;

const Calendar: FC = () => {
  const {
    state: { isPreview },
  } = useAppWrapperContext();
  const { state } = useProvider();
  const { showModal } = useModal();
  const [query, setQuery] = useQueryParams<CalendarQueryKeys>({
    view: 'month',
    year: dayjs().year().toString(),
    month: (dayjs().month() + 1).toString(),
    day: dayjs().date().toString(),
  });
  const [drilldownFrom, setDrilldownFrom] = useState<CalendarView | null>(null);

  const canViewLeadsAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.LEADS,
  );
  const canViewLeadsOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.LEADS,
  );
  const canViewEventsAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.EVENTS,
  );
  const canViewEventsOnly = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_USER,
    PERMISSION_KEYS.EVENTS,
  );

  const INIT_FILTERS = {
    eventTypes: Object.keys(EVENT_TYPES).map((type) => type),
    selectedDate: dayjs().toDate(),
    tab: CALENDAR_TABS[0],
    guests: [],
  } as unknown as Params;

  const [filters, setFilters] = useState(INIT_FILTERS);
  const [contactsSearch, setContactsSearch] = useState('');
  const { data: subscription } = useGetSubscription();
  const { data: events, isLoading } = useGetEvents({
    ...filters,
    tab: query.view as EVENT_TABS,
    selectedDate: dayjs(
      `${query.year}-${query.month}-${query.day}`,
    ).toISOString(),
    enabled: canViewEventsAll || canViewEventsOnly,
  });
  const { data: leadsData } = useGetLeads({
    keyword: contactsSearch,
    enabled: canViewLeadsAll || canViewLeadsOnly,
  });

  const value = useMemo(
    () => ({
      data: events,
      filters,
      tabs: CALENDAR_TABS,
    }),
    [events, filters],
  );

  const handleOnCreateEvent = useCallback(() => {
    showModal({
      title: 'Create Event',
      content: <CreateEventModal />,
      disableOutsideClick: true,
    });
  }, [showModal]);

  const handleEventTypes = (
    prevValues: any,
    isChecked: boolean,
    type: string,
  ) =>
    isChecked
      ? [...prevValues, type]
      : [...prevValues].filter((tp) => tp !== type);

  const contactItems = useMemo(
    () =>
      leadsData?.data.map((lead) => ({
        photoUrl: lead.photoUrl,
        itemLabel: lead.fullName,
        description: lead.emailAddress,
        value: lead.id,
      })) ?? ([] as AvatarOption[]),
    [leadsData],
  );

  const defaultContacts = useCallback(
    (values: string[]) =>
      contactItems?.filter((i) => values?.includes(i.value)),
    [contactItems],
  );

  const createEventButton = useMemo(() => {
    const event = (
      <Button
        onClick={handleOnCreateEvent}
        disabled={isLoading || subscription?.isExpired}
        label="CREATE EVENT"
        className="w-[274px]"
        expiredSubscription={subscription?.isExpired}
        tooltipPosition="right"
        data-tut={TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.FORM.CREATE_BUTTON}
      />
    );

    if (isPreview) return event;

    return (
      <CanRolePermissions I={ACTIONS.CREATE} this={PERMISSION_KEYS.EVENTS}>
        {event}
      </CanRolePermissions>
    );
  }, [handleOnCreateEvent, isLoading, isPreview, subscription?.isExpired]);

  const selectedDate = useMemo(() => {
    return dayjs(`${query.year}-${query.month}-${query.day}`).toDate();
  }, [query.year, query.month, query.day]);

  const handleDatePickerSelectDate = (d: Date) => {
    setQuery((searchParams) => {
      const date = dayjs(d);
      const year = `${date.get('y')}`;
      const month = `${date.get('month') + 1}`;
      const day = `${date.get('D')}`;
      const dayParam = query.day;
      const viewParam = query.view;

      if (dayParam === day && viewParam !== 'day') {
        setDrilldownFrom(viewParam as CalendarView);
        searchParams.set('view', 'day');
      } else if (dayParam === day && viewParam === 'day') {
        if (drilldownFrom) {
          searchParams.set('view', drilldownFrom);
        }
        setDrilldownFrom(null);
      }

      searchParams.set('year', year);
      searchParams.set('month', month);
      searchParams.set('day', day);
      return searchParams;
    });
  };

  const handleNavigateAndDrilldown: (date: Date, view: View) => void = (
    d,
    view,
  ) => {
    const date = dayjs(d);
    const year = `${date.get('y')}`;
    const month = `${date.get('month') + 1}`;
    const day = `${date.get('D')}`;
    setDrilldownFrom(null);

    setQuery(() => ({
      year,
      month,
      day,
      view,
    }));
  };
  return (
    <div className="flex flex-col">
      <CalendarContext.Provider value={value}>
        <h4 className="font-semibold">Calendar</h4>
        <div className="flex-1">
          <FilterForm
            initialValues={filters}
            onChange={(vals) => setFilters(vals as Params)}
          >
            {({ values, handleChange }) => (
              <>
                <div className="relative flex gap-x-[22px]">
                  <div className="space-y-4">
                    <div
                      className={cn(
                        'flex justify-between items-center',
                        'pt-[18px]',
                      )}
                    >
                      <div className="flex items-center gap-[108px]">
                        {createEventButton}
                      </div>
                    </div>
                    <div
                      className={cn(
                        'rounded-[6px] bg-fill-tertiary',
                        'px-[22px] py-[14px]',
                        'shadow-leapify-sm',
                      )}
                      data-tut={
                        TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.MANAGE.DATE
                      }
                    >
                      <DatePicker
                        selected={selectedDate}
                        inline
                        allowSameDay
                        onChange={handleDatePickerSelectDate}
                        renderCustomHeader={({
                          date,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <div
                            className={cn(
                              'px-4',
                              'flex items-center justify-between',
                              'border-b border-separator-primary pb-2',
                            )}
                          >
                            <p className="text-sm font-semibold">
                              {date.toLocaleString('default', {
                                month: 'long',
                              })}{' '}
                              {date.getFullYear()}
                            </p>
                            <div className="flex items-center gap-x-5">
                              <button
                                type="button"
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className="text-content"
                              >
                                <MdNavigateBefore className="text-lg" />
                              </button>
                              <button
                                type="button"
                                className={cn(
                                  'text-primary',
                                  'disabled:bg-transparent disabled:text-disabled',
                                )}
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                              >
                                <MdNavigateNext className="text-lg" />
                              </button>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <div
                      className={cn(
                        'rounded-[6px] bg-fill-tertiary',
                        'px-[22px] pb-[22px] w-[274px]',
                        'shadow-leapify-sm',
                      )}
                    >
                      <div
                        className={cn(
                          'w-full',
                          'py-[16px]',
                          'border-b-[#E4EDF0]',
                          'border-b-[0.5px]',
                          'mb-[12px]',
                        )}
                      >
                        <p className="font-semibold text-sm">
                          Search for people
                        </p>
                      </div>
                      <SelectAvatarPopover
                        selected={defaultContacts(values.guests)}
                        size={SIZE.Small}
                        values={contactItems}
                        onInputChange={(val) => setContactsSearch(val)}
                        onSelect={(val) => {
                          handleChange('guests', values.guests.concat(val));
                        }}
                        onClickSelected={(selected) =>
                          handleChange(
                            'guests',
                            values.guests.filter(
                              (guest: string) => guest !== selected,
                            ),
                          )
                        }
                        onClear={() => handleChange('guests', [])}
                        placeholder="Search for people"
                      />
                    </div>
                    <div
                      className={cn(
                        'rounded-[6px] bg-fill-tertiary',
                        'px-[22px] pb-[22px]',
                        'shadow-leapify-sm',
                      )}
                    >
                      <div
                        className={cn(
                          'w-full',
                          'py-[16px]',
                          'border-b-[#E4EDF0]',
                          'border-b-[0.5px]',
                          'mb-[12px]',
                        )}
                      >
                        <p className="font-semibold text-sm">Event type</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Checkbox
                          label="Virtual"
                          checked={values.eventTypes.includes(
                            EVENT_TYPES.VIDEO,
                          )}
                          checkboxClassname={
                            values.eventTypes.includes(EVENT_TYPES.VIDEO) &&
                            'border-[#F200B1]'
                          }
                          fill="text-[#F200B1]"
                          onChange={(e) => {
                            handleChange(
                              'eventTypes',
                              handleEventTypes(
                                values.eventTypes,
                                e.target.checked,
                                EVENT_TYPES.VIDEO,
                              ),
                            );
                          }}
                        />
                        <Checkbox
                          label="On Site"
                          checked={values.eventTypes.includes(
                            EVENT_TYPES.EVENT,
                          )}
                          onChange={(e) => {
                            handleChange(
                              'eventTypes',
                              handleEventTypes(
                                values.eventTypes,
                                e.target.checked,
                                EVENT_TYPES.EVENT,
                              ),
                            );
                          }}
                        />
                        <Checkbox
                          label="Mobile"
                          checked={values.eventTypes.includes(EVENT_TYPES.CALL)}
                          checkboxClassname={
                            values.eventTypes.includes(EVENT_TYPES.CALL) &&
                            'border-[#244EE0]'
                          }
                          fill="text-[#244EE0]"
                          onChange={(e) => {
                            handleChange(
                              'eventTypes',
                              handleEventTypes(
                                values.eventTypes,
                                e.target.checked,
                                EVENT_TYPES.CALL,
                              ),
                            );
                          }}
                        />
                        <Checkbox
                          label="Bookings"
                          checked={values.eventTypes.includes(
                            EVENT_TYPES.BOOKING,
                          )}
                          checkboxClassname={
                            values.eventTypes.includes(EVENT_TYPES.BOOKING) &&
                            'border-[#7740F4]'
                          }
                          fill="text-[#7740F4]"
                          onChange={(e) => {
                            handleChange(
                              'eventTypes',
                              handleEventTypes(
                                values.eventTypes,
                                e.target.checked,
                                EVENT_TYPES.BOOKING,
                              ),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      'big-calendar flex-1',
                      state?.isOpenBanner
                        ? 'h-[calc(100vh-320px)]'
                        : 'h-[calc(100vh-262px)]',
                    )}
                  >
                    <BigCalendar
                      defaultDate={selectedDate}
                      defaultView={query.view as View}
                      date={selectedDate}
                      view={query.view as View}
                      onView={(view) => {
                        setQuery((searchParams) => {
                          searchParams.set('view', view);
                          handleChange('tab', view.toUpperCase() as EVENT_TABS);
                          return searchParams;
                        });
                        handleChange('tab', view.toUpperCase() as EVENT_TABS);
                      }}
                      onSelectSlot={(slotInfo) => {
                        if (query.view === 'month') return;
                        const { start, end } = slotInfo;
                        showModal({
                          title: 'Create Event',
                          content: (
                            <CreateEventModal
                              initialStart={start}
                              initialEnd={end}
                            />
                          ),
                          disableOutsideClick: false,
                        });
                      }}
                      onDrillDown={handleNavigateAndDrilldown}
                      handleNavigate={handleNavigateAndDrilldown}
                    />
                  </div>
                </div>
              </>
            )}
          </FilterForm>
        </div>
      </CalendarContext.Provider>
    </div>
  );
};

export default Calendar;
